import { Helmet } from "react-helmet-async";
import { getTrackCoverUrl } from "@/utils/trackCoverUtils";
import { useSEOSettings } from "@/hooks/useSEOSettings";

interface SEOMetaProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: "website" | "music.song" | "music.album" | "article";
  siteName?: string;
  musician?: string;
  releaseDate?: string;
  songs?: string[];
  // music.song only — duration in whole seconds. Omit the og:music:duration
  // tag entirely when unknown rather than publish a wrong value.
  durationSeconds?: number;
  // music.song only — canonical URL of the music.album this track belongs
  // to. Most uploads are standalone singles with no album, so this is left
  // unset (and the tag omitted) far more often than not.
  albumUrl?: string;
}

export function SEOMeta({
  title,
  description,
  image,
  url,
  type = "website",
  siteName: siteNameProp,
  musician,
  releaseDate,
  songs,
  durationSeconds,
  albumUrl,
}: SEOMetaProps) {
  const seoSettings = useSEOSettings();
  const resolvedSiteName = siteNameProp || seoSettings.siteName;
  const fullTitle = title.includes(resolvedSiteName)
    ? title
    : `${title} - ${resolvedSiteName}`;
  const currentUrl = url || window.location.href;
  const metaImage = image
    ? getTrackCoverUrl(image)
    : seoSettings.ogImageUrl || undefined;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      {seoSettings.siteAuthor && (
        <meta name="author" content={seoSettings.siteAuthor} />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={resolvedSiteName} />
      {metaImage && <meta property="og:image" content={metaImage} />}
      {metaImage && (
        <meta property="og:image:alt" content={`Cover art for ${title}`} />
      )}
      {metaImage && <meta property="og:image:width" content="1200" />}
      {metaImage && <meta property="og:image:height" content="630" />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      {seoSettings.twitterHandle && (
        <meta name="twitter:site" content={seoSettings.twitterHandle} />
      )}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {metaImage && <meta name="twitter:image" content={metaImage} />}
      {metaImage && (
        <meta name="twitter:image:alt" content={`Cover art for ${title}`} />
      )}

      {/* Music-specific meta tags */}
      {type === "music.song" && (
        <>
          {durationSeconds !== undefined && (
            <meta
              property="music:duration"
              content={String(Math.round(durationSeconds))}
            />
          )}
          {albumUrl && <meta property="music:album" content={albumUrl} />}
        </>
      )}
      {type === "music.album" && (
        <>
          {musician && <meta property="music:musician" content={musician} />}
          {releaseDate && (
            <meta property="music:release_date" content={releaseDate} />
          )}
          {songs?.map((songUrl, i) => (
            <meta key={i} property="music:song" content={songUrl} />
          ))}
        </>
      )}
    </Helmet>
  );
}
